import { useCallback } from "react";
import { API, Auth } from "aws-amplify";
import counties from "util/counties";

const reportHeading = (payerMix) => {
  if (!payerMix) return "";
  switch (payerMix) {
    case "commercial":
      return "Commercial";
    case "medicare":
      return "Medicare";
    case "commercial-and-medicare":
      return "Commercial and Medicare";
    default:
      return "";
  }
};

async function getToken() {
  // Ensure Amplify session exists before any API call
  await Auth.currentAuthenticatedUser();
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (err) {
    console.error("error getting token", err);
  }
}

function useOpenQueryBridge({ history, payerMix, getToken, setShowResubmitModal }) {
  const requestToken = useCallback(async () => {
    const token = await getToken();
    return token;
  }, [getToken]);

  const getPayerMix = useCallback(() => payerMix, [payerMix]);

  const onMyQueryOpen = useCallback(() => {
    history.push("/openquery/queries");
    window.location.reload();
  }, [history]);

  const onQuerySubmitted = useCallback(() => {
    history.push(`/openquery/${getPayerMix()}`);
  }, [history]);

  const onClickCancelQueryReport = () => {
    setShowResubmitModal(false);
  }

  const startJob = useCallback(async ({ categoryId, definition }) => {
    if (!definition) {
      throw new Error("OPENQUERY_START_JOB missing definition");
    }
    try {
      await Auth.currentAuthenticatedUser();
      const response = await API.post("OpenQueryAPI", "/openquery/jobs", {
        body: definition
      });

      const status = response?.statusCode || response?.status || 200;

      return {
        ok: true,
        status,
        body: response,
        categoryId
      };
    } catch (err) {
      console.error("error submitting openquery job from parent", err);

      const status = err?.response?.status || err?.response?.statusCode || 500;

      const message =
        err?.response?.data?.message || err?.message || "Job submit failed";
      return {
        ok: false,
        status,
        body: { message },
        categoryId
      };
    }
  }, []);

  return { requestToken, getPayerMix, onQuerySubmitted, onMyQueryOpen, startJob, onClickCancelQueryReport };
}


function buildQueryBreadcrumbs({
  payerMix,
  selectedReportName,
  reportHeading,
}) {
  const items = [
    { label: "Home", to: "/" },
    { label: "Query", to: "/openquery" },
    { label: "Create New Query", to: "/openquery" },
    { label: reportHeading(payerMix), to: `/openquery/${payerMix}` },
  ];

  if (selectedReportName) {
    items.push({ label: selectedReportName });
  }

  return items;
}

// query detail form functions start
function formatDate(inputDate) {
  if (!inputDate) return '';
  return `${inputDate.getFullYear()}-${(inputDate.getMonth() + 1) < 10
    ? '0' + (inputDate.getMonth() + 1)
    : (inputDate.getMonth() + 1)
    }-${inputDate.getDate().toString().padStart(2, '0')}`;
}

function validateFileName(fileName) { // https://stackoverflow.com/questions/11100821/javascript-regex-for-validating-filenames
  const rg1 = /^[^\\/:,.*?"<>|]+$/; // forbidden characters \ / : , . * ? " < > |
  const rg2 = /^(nul|prn|con|lpt[0-9]|com[0-9])(\.|$)/i; // forbidden file names
  return rg1.test(fileName) && !rg2.test(fileName) && fileName.length < 255;
}

function disableSubmitButton(common) {
  // It validate filename
  // If there is no priorQueryParams then it is new query to submit ->  don't disable submit btn
  try {
    const prior = common?.priorQueryParams;
    if (!prior) return false;

    // 1) Filename check
    const currentFile = (common.fileName || "").trim();
    const priorFile = (prior.description || "").trim();
    const isFilenameSame = currentFile === priorFile;

    // disable btn if file name same
    return isFilenameSame;
  } catch (error) {
    return false;
  }
}

function getPriorDate(priorParams, uiField) {
  const map = {
    startDate: ["start_date", "start_date_first"],
    endDate: ["end_date", "end_date_first"],
    startDate2: ["measure_start_date", "start_date_second"],
    endDate2: ["measure_end_date", "end_date_second"],
  };

  const keys = map[uiField] || [];
  for (let key of keys) {
    if (priorParams[key]) return priorParams[key];
  }
   
  return null;
}


function getOneYearsBack(base = new Date()) {
  const d = new Date(base);
  d.setFullYear(d.getFullYear() - 1);
  return d;
}

function parseCommaSeparatedInput(input) {
  return input
    .split(',')
    .reduce((acc, item) => {
      let retItem = item.replace(/\s/g, '');
      if (retItem) {
        acc.push(retItem);
      }
      return acc;
    }, []);
}

function underNpiLimit(input) {
  return parseCommaSeparatedInput(input).length <= 1000;
}

function underCodesLimit(input) {
  return parseCommaSeparatedInput(input).length <= 20;
}

function validateNpis(npis) {
  if (!npis || !npis.trim()) return true; // tolerate empty strings
  let isValid = true;
  const splitNpis = parseCommaSeparatedInput(npis);
  for (let i = 0; i < splitNpis.length; i++) {
    // Check if the input is exactly 10 digits
    if (!/^[0-9]{10}$/.test(splitNpis[i])) {
      isValid = false;
      break;
    }

    // Prepend the prefix
    const fullCode = "80840" + splitNpis[i];
    let sum = 0;
    const parity = fullCode.length % 2;

    for (let j = 0; j < fullCode.length; j++) {
      let d = parseInt(fullCode[j], 10);

      if (j % 2 === parity) {
        d *= 2;
      }
      if (d > 9) {
        d -= 9;
      }

      sum += d;
    }

    if (sum % 10 !== 0) {
      isValid = false;
      break;
    }
  };
  return isValid;
}

function joinCounties(countiesAsCodes, withoutCode) {
  var joinedCounties = [];
  for (const countyCode of countiesAsCodes) {
    if (withoutCode) {
      joinedCounties.push(counties.find(({ code }) => code === countyCode).name);
    } else {
      joinedCounties.push(
        countyCode + ' - ' + counties.find(({ code }) => code === countyCode).name
      );
    }
  }
  return joinedCounties.join('; ');
}

function addOrRemoveCounty(checkedCounties, newCounty, setCheckedCounties) {
  const index = checkedCounties.indexOf(newCounty);
  if (index > -1) {
    return setCheckedCounties(checkedCounties.filter((county) => county !== newCounty));
  } else {
    return setCheckedCounties([...checkedCounties, newCounty]);
  }
}

function stripPeriods(input) {
  return input.replaceAll('.', '');
}

// query detail form functions end

export {
  reportHeading,
  getToken,
  useOpenQueryBridge,
  buildQueryBreadcrumbs,
  formatDate,
  validateFileName,
  disableSubmitButton,
  getOneYearsBack,
  underNpiLimit,
  validateNpis,
  joinCounties,
  addOrRemoveCounty,
  underCodesLimit,
  stripPeriods,
  getPriorDate
};
